/**
 * Paginated chat thread list API client.
 */
import { buildApiUrl, tenantApiPrefix } from '../config';
import type { CopilotThread } from '../pages/copilot/stores/useCopilotStore';

export interface ThreadListResponse {
  threads: CopilotThread[];
  total: number;
  has_more: boolean;
}

export interface FetchThreadListParams {
  offset?: number;
  limit?: number;
  q?: string;
  /** Defaults to copilot + playbook threads. */
  agentTypes?: string[];
}

function authHeaders(): Record<string, string> {
  const token = window.localStorage.getItem('klaris_jwt');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function mapThreadRow(row: Record<string, unknown>): CopilotThread {
  return {
    threadId: String(row.thread_id),
    title: (row.title as string | null) ?? null,
    agentType: (row.agent_type as string) || 'copilot',
    createdAt: (row.created_at as string | null) ?? null,
  };
}

export async function fetchThreadList(
  tenantId: string,
  params: FetchThreadListParams = {},
): Promise<ThreadListResponse> {
  const {
    offset = 0,
    limit = 10,
    q,
    agentTypes = ['copilot', 'playbook'],
  } = params;

  const search = new URLSearchParams();
  search.set('offset', String(offset));
  search.set('limit', String(limit));
  if (agentTypes.length > 0) {
    search.set('agent_type', agentTypes.join(','));
  }
  if (q?.trim()) {
    search.set('q', q.trim());
  }

  const resp = await fetch(
    buildApiUrl(`${tenantApiPrefix(tenantId)}/chat/threads?${search.toString()}`),
    { headers: authHeaders(), cache: 'no-store' } as RequestInit,
  );
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const detail = (data && (data.detail || data.message)) || `Request failed (${resp.status})`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }

  const rows = Array.isArray(data?.threads) ? data.threads : [];
  return {
    threads: rows.map((row: Record<string, unknown>) => mapThreadRow(row)),
    total: typeof data?.total === 'number' ? data.total : rows.length,
    has_more: Boolean(data?.has_more),
  };
}
